"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/routing";
import { getDesignCount, subscribeDesigns } from "@/lib/designs";
import { useAccount } from "@/components/account/AccountProvider";

/**
 * Header entry to a GUEST's own designs, which live in this browser rather than in an account.
 * Hidden until this browser has actually made one, so a first visit never leads to an empty page,
 * and hidden again once somebody signs in, because AccountLink then covers the same ground.
 * Always a word, never an icon: nothing pictures "the posters you made" clearly enough. When the
 * header runs out of room it is the create link that gives way (see the layout's container query).
 */
export function MyDesignsLink({ className = "" }: { className?: string }) {
  const t = useTranslations("common");
  const { account } = useAccount();
  const [count, setCount] = useState(0);

  useEffect(() => {
    setCount(getDesignCount());
    return subscribeDesigns(() => setCount(getDesignCount()));
  }, []);

  // Signed in, the account link takes over: /account holds the orders AND the designs, and two nav
  // entries pointing at overlapping lists is one too many on the phone widths where the labels
  // already have to fight for room.
  if (account) return null;
  if (count === 0) return null;

  return (
    <Link className={`whitespace-nowrap ${className}`} data-testid="nav-my-designs" href="/my-designs">
      {t("nav.myDesigns")}
    </Link>
  );
}
